'use client';

import {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Autocomplete,
  type AutocompleteFieldProps,
  type AutocompleteOption,
} from '@kit/ui/autocomplete';

import { searchOrganizationsWithLogosAction } from '../server/organization-search-actions';
import { OrganizationAvatar } from './organization-avatar';
import { OrganizationRow } from './organization-row';
import type { OrganizationSummary } from './organization-types';

export interface OrganizationPickerProps extends AutocompleteFieldProps {
  /** Controlled selection: an id (single) or ids (multiple). */
  value?: string | string[];
  /** Multi-select with a removable-chip row. Default false (single). */
  multiple?: boolean;
  /** Fires with the id(s) for form binding. */
  onChange?: (value: string | string[] | null) => void;
  /** Fires with the FULL entity on each add — for side-effects (type-lock, ranks). */
  onSelect?: (organization: OrganizationSummary) => void;
  /** Fires with the FULL entity on each remove. */
  onDeselect?: (organization: OrganizationSummary) => void;
  /**
   * Display data for ids already in `value` at mount (edit mode) so chips /
   * the collapsed single-select summary render names + logos, not raw ids.
   */
  initialSelected?: OrganizationSummary[];
  /**
   * Static option set. When provided, the picker skips the async server search
   * and filters this list client-side.
   */
  options?: OrganizationSummary[];
  /** Ids to hide from the result list (e.g. orgs already on the roster). */
  excludeIds?: string[];
  /** Allow creating an (unverified) organization from the typed text. */
  creatable?: boolean;
  onCreate?: (
    name: string,
  ) => OrganizationSummary | Promise<OrganizationSummary>;
  /** Optional cap on how many organizations can be selected (multiple only). */
  maxSelections?: number;
  /** Clear the input after a selection (action-picker vs bound field). */
  clearOnSelect?: boolean;
  /** Context slot rendered above the search. */
  header?: ReactNode;
  /**
   * Override the server search. Defaults to the canonical published-only
   * `searchOrganizationsWithLogosAction`, so every existing caller is
   * unchanged. The dancer experience form passes the draft-aware variant
   * (`searchExperienceOrganizationsWithLogosAction`) so unverified drafts are
   * pickable and near-duplicate minting stops; admin surfaces pass
   * `adminSearchOrganizationsWithLogosAction` to also reach unpublished orgs.
   * Must return the resolved `OrganizationSummary[]` shape (logos resolved).
   *
   * Every shipped variant yields `organizations.id`, and a replacement MUST
   * too: this picker's `onChange` value is persisted straight into columns that
   * reference `organizations (id)`. A search returning `accounts.id` type-checks
   * identically (both are `string`, both pass `z.uuid()`) and fails only at
   * write time with FK `23503` — the BAL-908 regression, which is why the
   * former account-domain `superAdmin` flag was removed rather than documented.
   */
  searchAction?: (input: {
    query: string;
    limit?: number;
  }) => Promise<OrganizationSummary[]>;
  placeholder?: string;
  emptyMessage?: string;
  minChars?: number;
  /** Max server results per search (default 8). */
  searchLimit?: number;
  disabled?: boolean;
  className?: string;
  /** Visible label rendered by the primitive (linked to the input). */
  label?: string;
}

/**
 * THE canonical organization picker — single or multi. A thin wrapper over
 * `@kit/ui/autocomplete` (the BAL-347 canonical searchable-select) that renders
 * the shared rich `OrganizationRow`, self-fetches via the one canonical
 * `searchOrganizationsWithLogosAction`, and lets the primitive own chips + the
 * selection cap (this wrapper renders NO strip of its own). On single-select it
 * supplies `renderSelected` for the collapsed org summary card. Mobile
 * bottom-sheet, keyboard nav, ARIA are inherited. Mirror of `ProfilePicker`.
 *
 * Two callback tiers:
 * - `onChange(ids)` — for react-hook-form value binding.
 * - `onSelect(entity)` / `onDeselect(entity)` — hand back the FULL organization
 *   so a parent can run a side-effect (lock the org type, load ranks) without a
 *   second round-trip.
 */
export const OrganizationPicker = forwardRef<
  HTMLDivElement,
  OrganizationPickerProps
>(
  (
    {
      value,
      multiple = false,
      onChange,
      onSelect,
      onDeselect,
      initialSelected,
      options: staticOptions,
      excludeIds,
      creatable,
      onCreate,
      maxSelections,
      clearOnSelect,
      header,
      searchAction = searchOrganizationsWithLogosAction,
      placeholder,
      emptyMessage,
      minChars = 2,
      searchLimit,
      disabled,
      className,
      label,
      'aria-label': ariaLabel,
      ...fieldProps
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isStatic = Array.isArray(staticOptions);

    const [results, setResults] = useState<OrganizationSummary[]>(
      staticOptions ?? [],
    );
    const [isFetching, setIsFetching] = useState(false);
    const [searchTooShort, setSearchTooShort] = useState(true);

    // Guards against out-of-order async responses (last request wins).
    const requestIdRef = useRef(0);

    // Cache hits keyed by id so selected chips / the collapsed single summary
    // keep resolving after the option list changes. Seeded from
    // `initialSelected` + any static options.
    const [cache, setCache] = useState<Map<string, OrganizationSummary>>(
      () =>
        new Map(
          [...(initialSelected ?? []), ...(staticOptions ?? [])].map((o) => [
            o.id,
            o,
          ]),
        ),
    );

    // Synchronous mirror of `cache`. `setCache` commits asynchronously, but
    // `onCreate` caches the freshly-minted org and the primitive fires
    // `onSelect(resolve(newId))` in the SAME tick — so `resolve` must see the
    // new entry before React re-renders. Reading the ref closes that race;
    // without it `resolve` fell through to `{ name: id }` and the parent
    // persisted the org UUID as the manual name (BAL org-name-as-uuid bug).
    const cacheRef = useRef(cache);

    const cacheOrgs = useCallback((orgs: OrganizationSummary[]) => {
      if (orgs.length === 0) return;
      const next = new Map(cacheRef.current);
      for (const o of orgs) next.set(o.id, o);
      cacheRef.current = next; // sync: visible to a same-tick resolve()
      setCache(next); // async: re-render so chips/labels re-resolve
    }, []);

    const resolve = useCallback(
      (id: string): OrganizationSummary =>
        cache.get(id) ??
        cacheRef.current.get(id) ??
        results.find((r) => r.id === id) ?? {
          id,
          name: id,
          logoUrl: null,
        },
      [cache, results],
    );

    const selectedIds = useMemo<string[]>(
      () => (Array.isArray(value) ? value : value ? [value] : []),
      [value],
    );
    const prevIdsRef = useRef<string[]>(selectedIds);

    const handleInputChange = useCallback(
      async (query: string) => {
        if (isStatic) return; // client-side filter over static options
        const trimmed = query.trim();
        setSearchTooShort(trimmed.length < minChars);
        if (trimmed.length < minChars) {
          setResults([]);
          return;
        }
        const reqId = ++requestIdRef.current;
        setIsFetching(true);
        try {
          const hits = await searchAction({
            query: trimmed,
            limit: searchLimit,
          });
          if (reqId !== requestIdRef.current) return; // stale response
          setResults(hits);
          cacheOrgs(hits);
        } finally {
          if (reqId === requestIdRef.current) setIsFetching(false);
        }
      },
      [cacheOrgs, isStatic, minChars, searchAction, searchLimit],
    );

    const toOption = useCallback(
      (o: OrganizationSummary): AutocompleteOption => ({
        value: o.id,
        label: o.name,
        description: o.handle ? `@${o.handle}` : undefined,
        icon: (
          <OrganizationAvatar
            name={o.name}
            logoUrl={o.logoUrl}
            className="h-5 w-5"
          />
        ),
      }),
      [],
    );

    // Options = current result set PLUS any selected-but-not-in-results entries,
    // so the primitive can resolve chip labels/logos for ids the current search
    // no longer returns (edit mode, post-query-change).
    const options = useMemo<AutocompleteOption[]>(() => {
      const excluded = new Set(excludeIds ?? []);
      const source = (isStatic ? (staticOptions ?? []) : results).filter(
        (o) => !excluded.has(o.id),
      );
      const byId = new Map(source.map((o) => [o.id, o]));
      // Selected ids always resolve (so their chip/summary renders) even if excluded.
      for (const id of selectedIds) {
        if (!byId.has(id)) byId.set(id, resolve(id));
      }
      return [...byId.values()].map(toOption);
    }, [
      excludeIds,
      isStatic,
      results,
      staticOptions,
      selectedIds,
      resolve,
      toOption,
    ]);

    const fireDiff = useCallback(
      (nextIds: string[]) => {
        const prev = prevIdsRef.current;
        const prevSet = new Set(prev);
        const nextSet = new Set(nextIds);
        for (const id of nextIds) if (!prevSet.has(id)) onSelect?.(resolve(id));
        for (const id of prev) if (!nextSet.has(id)) onDeselect?.(resolve(id));
        prevIdsRef.current = nextIds;
      },
      [onDeselect, onSelect, resolve],
    );

    const handleChange = useCallback(
      (next: string | string[] | null) => {
        if (multiple) {
          const ids = Array.isArray(next) ? next : next ? [next] : [];
          fireDiff(ids);
          onChange?.(ids);
        } else {
          const id = Array.isArray(next) ? (next[0] ?? null) : next;
          fireDiff(id ? [id] : []);
          onChange?.(id);
        }
      },
      [fireDiff, multiple, onChange],
    );

    const handleCreate = useMemo(() => {
      if (!creatable || !onCreate) return undefined;
      return async (inputValue: string): Promise<AutocompleteOption> => {
        const created = await onCreate(inputValue);
        cacheOrgs([created]);
        return toOption(created);
      };
    }, [cacheOrgs, creatable, onCreate, toOption]);

    const renderOption = useCallback(
      (option: AutocompleteOption, isSelected: boolean) => (
        <OrganizationRow
          organization={resolve(option.value)}
          trailing={
            isSelected ? (
              <span className="text-primary text-xs">✓</span>
            ) : undefined
          }
        />
      ),
      [resolve],
    );

    // Single-select: rich collapsed summary card (logo + name + type + verified).
    const renderSelected = useCallback(
      (option: AutocompleteOption | null) =>
        option ? (
          <OrganizationRow organization={resolve(option.value)} />
        ) : null,
      [resolve],
    );

    const resolvedEmpty =
      emptyMessage ??
      (!isStatic && searchTooShort
        ? t('common:typeToSearch', 'Type to search…')
        : t('common:noResults', 'No results'));

    return (
      <div ref={ref} className={className}>
        <Autocomplete
          {...fieldProps}
          label={label}
          multiple={multiple}
          maxSelections={maxSelections}
          header={header}
          clearOnSelect={clearOnSelect ?? multiple}
          serverFiltered={!isStatic}
          value={multiple ? selectedIds : (selectedIds[0] ?? undefined)}
          onChange={handleChange}
          options={options}
          loading={isFetching}
          disabled={disabled}
          onInputChange={isStatic ? undefined : handleInputChange}
          renderOption={renderOption}
          renderSelected={multiple ? undefined : renderSelected}
          creatable={creatable}
          onCreate={handleCreate}
          minChars={minChars}
          placeholder={
            placeholder ?? t('common:searchOrganizations', 'Search…')
          }
          emptyMessage={resolvedEmpty}
          aria-label={ariaLabel}
        />
      </div>
    );
  },
);

OrganizationPicker.displayName = 'OrganizationPicker';
